/**
 * VisualProgram bed (signage rotation) decision core, extracted verbatim from
 * use-playback's syncVideo/advanceVideo/peekNextVideo so the screen-bus
 * selection logic is unit-testable without a DOM. The hook applies the
 * returned plan to its refs/state and keeps every side effect (element loop
 * flag, failure-budget resets, React state).
 */

import type {
  ChannelType,
  DeviceMediaItem,
  DeviceSync,
  SlotSignageContent,
} from "@workspace/api-client-react";
import {
  buildRotation,
  mediaAssetById,
  isSlotActive,
  playlistById,
  screenSlotAt,
  syncShuffleBase,
} from "@workspace/player-core";

/** The video/signage channel renders both motion (video) and still (image) media. */
export const isSignage = (i: DeviceMediaItem): boolean =>
  i.type === "video" || i.type === "image";

/** One evaluation of the screen bus against the sync payload's timeline. */
export type VideoSyncPlan =
  /** No active screen slot: reset the rotation; the takeover may keep the surface. */
  | { kind: "inactive"; signage: SlotSignageContent | null }
  | {
      kind: "active";
      signage: SlotSignageContent | null;
      /** Slot occupancy key (playlistId ?? mediaAssetId). */
      slotKey: string | null;
      /** True when the occupancy changed: adopt `items` and restart at 0. */
      rotationChanged: boolean;
      /** Fresh signage rotation — only meaningful when rotationChanged. */
      items: DeviceMediaItem[];
      playlistName: string | null;
      /** Per-entry still-image duration override from the slot. */
      imageDisplaySec: number | null;
    };

/**
 * Decide what the signage bed should do for the screen-timeline window
 * containing `nowMs`. The screen timeline is authoritative when present: the
 * window containing NOW wins, so scheduled signage transitions land on time
 * even mid-payload — the plan's now slot is only a poll-time snapshot
 * (CB-024/CB-028). Web page / widget slots have no media refs (isSlotActive
 * is false); the hydrated payload rides the slot itself.
 */
export function planVideoSync(
  sync: DeviceSync | null,
  nowMs: number,
  previousSlotKey: string | null,
): VideoSyncPlan {
  const slot = screenSlotAt(sync, nowMs);
  const signage =
    slot && slot.source !== "none" && slot.signageContent
      ? slot.signageContent
      : null;
  if (!isSlotActive(slot)) {
    return { kind: "inactive", signage };
  }

  const slotKey = slot?.playlistId ?? slot?.mediaAssetId ?? null;
  const shuffleBase = syncShuffleBase(sync);
  let items: DeviceMediaItem[] = [];
  let playlistName: string | null = slot?.playlistName ?? null;
  if (slot?.mediaAssetId) {
    const item = mediaAssetById(sync, slot.mediaAssetId);
    if (item && isSignage(item)) items = [item];
  } else {
    const playlist = playlistById(sync, slot?.playlistId);
    items = playlist
      ? buildRotation(playlist, shuffleBase).filter(isSignage)
      : [];
    playlistName = playlist?.name ?? playlistName;
  }

  return {
    kind: "active",
    signage,
    slotKey,
    rotationChanged: previousSlotKey !== slotKey,
    items,
    playlistName,
    imageDisplaySec: slot?.imageDisplayDurationSec ?? null,
  };
}

/**
 * Does a schedule-boundary transition on `channelType` have to FORGET the
 * channel's remembered occupancy, so the next sync re-selects from scratch?
 *
 * Yes for the audio channels: they resolve from `plan.channels[].now`, a
 * poll-time snapshot, so a daypart edge has to force a re-select or the
 * expired slot plays on.
 *
 * NO for the screen bus, and this is load-bearing. `planVideoSync` resolves
 * the `screenTimeline` window containing NOW by itself and reports a genuine
 * occupancy change through `slotKey`, so it needs no hint — while the server
 * emits one screen window per playlist ITEM, so "forget" here fired every few
 * seconds. That reset the rotation to index 0 at every item edge: a still lost
 * most of its dwell, item 3 of a longer playlist was never reachable, and an
 * edge landing inside the crossfade's outgoing linger (i.e. exactly when a
 * video `ended`, which is where an item window ends) re-applied the item that
 * had just finished — React re-used the outgoing layer's element by key,
 * `autoPlay` never ran again, and the screen sat on a frozen last frame with
 * no event left to advance it.
 */
export function boundaryForgetsOccupancy(channelType: ChannelType): boolean {
  return channelType !== "video";
}

/** Index the backdrop rotation advances to (wraps; 0 stays 0 on empty). */
export function nextVideoIndex(index: number, length: number): number {
  return length === 0 ? 0 : (index + 1) % length;
}

/**
 * Peek the item the backdrop rotation will show next (for preloading). A
 * rotation shorter than two items has nothing distinct to preload.
 */
export function peekNextVideoItem(
  rotation: readonly DeviceMediaItem[],
  index: number,
): DeviceMediaItem | null {
  if (rotation.length < 2) return null;
  return rotation[(index + 1) % rotation.length] ?? null;
}
